import { useTranslation } from 'react-i18next';
import { type Location, Outlet, useLocation } from 'react-router-dom';

import { ApplicationContextProvider, BaseApplicationProperties } from '@n2-common/components/ApplicationContext';
import { HeaderContainer, PageContainer, StepList, UiContextProps, UiContextProvider } from '@n2-common/ui';
import { ApplicationPaths } from '../ApplicationFormsHelpers';
import React, { useMemo } from 'react';
import { ArrowLeftIcon } from '@n2-common/myPagesUi/components/icons';
import style from './style.module.scss';
import { Channel } from '@n2-common/serverGeneratedTypings/channel';

const Steps = ['application', 'offer', 'aditional-info', 'signing'];

type PageLayoutProps = BaseApplicationProperties & { showProgressBar?: (location: Location) => boolean } & UiContextProps;

export const PageLayout = ({
    antiforgeryToken = '',
    productType,
    channel,
    versionApp,
    country,
    showProgressBar = pathShouldHaveTopProgressBar,
}: PageLayoutProps) => {
    const { t } = useTranslation('common');
    const location = useLocation();

    const stepListProps = useMemo(() => {
        if (!showProgressBar(location)) {
            return null;
        }
        return {
            steps: Steps.map((s) => ({
                name: t<string>(`steps.${s}`),
            })),
            current: getStepFromPath(location),
        };
    }, [location, showProgressBar, t]);

    const onClickFunc = () => {
        window.location.href = '/';
    };

    return (
        <UiContextProvider channel={channel} versionApp={versionApp}>
            <ApplicationContextProvider antiforgeryToken={antiforgeryToken} country={country} productType={productType}>
                <HeaderContainer>{stepListProps && <StepList {...stepListProps} />}</HeaderContainer>
                <PageContainer>
                    {channel === Channel.MobileApp && (
                        <div className={style.backButton} tabIndex={0} onClick={() => onClickFunc()}>
                            <ArrowLeftIcon />
                            {t<string>('detailslayout.back')}
                        </div>
                    )}
                    <Outlet />
                </PageContainer>
            </ApplicationContextProvider>
        </UiContextProvider>
    );
};

function getStepFromPath(location: Location) {
    switch (location.pathname) {
        case ApplicationPaths.offer:
            return 1;
        case ApplicationPaths.cdd:
        case ApplicationPaths.insurance:
            return 2;
        case ApplicationPaths.sign:
        case ApplicationPaths.signDetails:
        case ApplicationPaths.setAccountNumber:
        case ApplicationPaths.uploadDocuments:
        case ApplicationPaths.signCompleted:
        case ApplicationPaths.sblCompleted:
        case ApplicationPaths.setPin:
        case ApplicationPaths.applicantActionsCompleted:
            return 3;
    }
    return 0;
}

function pathShouldHaveTopProgressBar(location: Location) {
    switch (location.pathname) {
        case ApplicationPaths.declined:
        case ApplicationPaths.declinedFromAgent:
        case ApplicationPaths.canceled:
        case ApplicationPaths.notFound:
        case ApplicationPaths.completed:
        case ApplicationPaths.applicantActionsCompleted:
        case ApplicationPaths.error:
            return false;
    }
    return true;
}
